/**
 * Getting Started Articles — Spanish
 */

import type { HelpArticle } from "../../../types";

export const GETTING_STARTED_ARTICLES_ES: HelpArticle[] = [
  {
    id: "gs-001",
    slug: "what-is-supabank",
    title: "¿Qué es Supabank?",
    description: "Descubre cómo Supabank te ayuda a convertir cripto en dinero fiat",
    category: "getting-started",
    popular: true,
    order: 1,
    keywords: ["introducción", "qué es", "resumen", "cripto", "fiat"],
    content: `
# ¿Qué es Supabank?

Supabank es una plataforma de liquidación de criptomonedas de nivel corporativo que te permite convertir cripto en dinero fiat y recibirlo directamente en tu cuenta bancaria.

## Cómo funciona

1. **Verifica tu identidad** — Completa la verificación KYC para acceder a todas las funciones
2. **Crea una dirección de liquidación** — Genera una dirección cripto única para depósitos
3. **Deposita cripto** — Envía criptomonedas compatibles a tu dirección
4. **Conversión automática** — Tu cripto se convierte automáticamente a fiat
5. **Recibe los fondos** — El dinero se transfiere a tu cuenta bancaria vinculada

## Características principales

- **Soporte multi-cadena** — Acepta depósitos de Ethereum, Polygon, Solana y más
- **Múltiples monedas** — Recibe pagos en USD, EUR o MXN
- **Conversión instantánea** — A tasas de mercado competitivas
- **Integración bancaria** — Transferencias directas a tu cuenta verificada
- **Disponibilidad 24/7** — Deposita y convierte en cualquier momento

## ¿Para quién es Supabank?

Supabank es ideal para:
- Empresas que necesitan convertir ingresos en cripto a fiat
- Freelancers que reciben pagos en criptomonedas
- Traders que buscan una salida eficiente a fiat
- Compañías que gestionan tesorería en cripto

## Primeros pasos

¿Listo para empezar? [Crea tu cuenta](/auth) y completa la verificación para comenzar a convertir cripto en fiat.
    `.trim(),
  },
  {
    id: "gs-002",
    slug: "creating-your-account",
    title: "Cómo crear tu cuenta",
    description: "Guía paso a paso para registrarte en Supabank",
    category: "getting-started",
    popular: true,
    order: 2,
    keywords: ["registro", "crear", "cuenta", "nueva", "alta"],
    content: `
# Cómo crear tu cuenta

Crear tu cuenta en Supabank solo te tomará unos minutos.

## Paso 1: Registro

1. Visita [supabank.xyz](/auth)
2. Introduce tu correo electrónico
3. Haz clic en "Continuar con correo"
4. Revisa tu bandeja de entrada para el enlace mágico
5. Haz clic en el enlace para verificar tu correo

## Paso 2: Completa tu perfil

Después de verificar tu correo:
1. Introduce tu nombre completo (como aparece en tu documento de identidad)
2. Proporciona tu fecha de nacimiento
3. Introduce tu dirección de residencia
4. Selecciona tu país de residencia

## Paso 3: Verifica tu identidad (KYC)

Para acceder a todas las funciones:
1. Sube un documento oficial (pasaporte, licencia de conducir o DNI)
2. Toma un selfie para verificación
3. Espera la aprobación (normalmente 2-5 minutos)

## Lo que necesitarás

- Correo electrónico válido
- Documento de identidad con foto
- Comprobante de domicilio (en algunas regiones)
- Smartphone o webcam para el selfie

## Tipos de cuenta

**Cuenta personal**
- Para uso individual
- Verificación estándar
- Límites iniciales moderados

**Cuenta empresarial**
- Para empresas y organizaciones
- Documentación adicional requerida
- Límites más altos disponibles

## Consejos para una aprobación rápida

- Usa una foto reciente y de buena calidad de tu documento
- Asegúrate de tener buena iluminación para el selfie
- Verifica que todo el texto sea legible
- Usa el nombre exactamente como aparece en tu documento

## Siguientes pasos

Una vez verificado, podrás:
- Añadir tu cuenta bancaria para retiros
- Crear direcciones de liquidación
- Empezar a recibir depósitos de cripto
    `.trim(),
  },
  {
    id: "gs-003",
    slug: "kyc-verification-process",
    title: "Proceso de verificación KYC",
    description: "Todo lo que necesitas saber sobre la verificación de identidad",
    category: "getting-started",
    popular: true,
    order: 3,
    keywords: ["kyc", "verificación", "identidad", "documentos", "id"],
    content: `
# Proceso de verificación KYC

La verificación KYC (Conoce a tu Cliente) es necesaria para usar los servicios de Supabank. Esto nos ayuda a cumplir con las regulaciones y proteger tu cuenta.

## Por qué es necesario el KYC

- Cumplimiento normativo (leyes AML/KYC)
- Prevención de fraude
- Seguridad de la cuenta
- Acceso a límites más altos

## Pasos de verificación

### 1. Información personal
- Nombre legal completo
- Fecha de nacimiento
- Dirección de residencia
- Nacionalidad

### 2. Carga de documentos

**Documentos aceptados:**
- Pasaporte (recomendado)
- Licencia de conducir
- DNI o cédula de identidad

**Requisitos del documento:**
- Debe estar vigente (no caducado)
- Foto clara y legible
- Las cuatro esquinas visibles
- Sin reflejos ni brillos

### 3. Verificación con selfie
- Toma una foto en vivo de tu rostro
- Debe coincidir con la foto del documento
- Buena iluminación requerida
- Sin gafas ni elementos que cubran el rostro

## Tiempos de verificación

| Estado | Tiempo típico |
|--------|---------------|
| Aprobación automática | 2-5 minutos |
| Revisión manual | Hasta 24 horas |
| Documentos adicionales | Variable |

## Problemas frecuentes

**¿Documento rechazado?**
- Asegúrate de que no esté caducado
- Repite la foto con mejor iluminación
- Verifica que el texto sea legible

**¿Selfie fallido?**
- Quítate las gafas y sombreros
- Mira directamente a la cámara
- Usa luz natural

## Niveles de verificación

| Nivel | Requisitos | Límites |
|-------|------------|---------|
| Básico | Verificación de correo | Solo consulta |
| Estándar | DNI + Selfie | $50,000/mes |
| Avanzado | Documentos adicionales | $500,000/mes |

## Privacidad y seguridad

- Los documentos se cifran en tránsito
- Almacenamiento seguro con acceso limitado
- Se eliminan después de la verificación (según política)
- Nunca se comparten con terceros

## ¿Necesitas ayuda?

Si tu verificación tarda más de lo esperado, contacta con nuestro equipo de soporte.
    `.trim(),
  },
  {
    id: "gs-004",
    slug: "supported-countries",
    title: "Países admitidos y restricciones",
    description: "Dónde están disponibles los servicios de Supabank",
    category: "getting-started",
    order: 4,
    keywords: ["países", "regiones", "restricciones", "disponibilidad", "admitidos"],
    content: `
# Países admitidos y restricciones

Supabank opera en la mayoría de países del mundo, con algunas excepciones debido a requisitos regulatorios.

## Regiones admitidas

Supabank está disponible en:
- **Norteamérica**: EE.UU. (mayoría de estados), Canadá
- **Europa**: Estados miembros de la UE, Reino Unido, Suiza
- **Asia-Pacífico**: Japón, Singapur, Hong Kong, Australia
- **Latinoamérica**: México, Brasil, Argentina, Colombia, Chile
- **Otros**: La mayoría de países no listados como restringidos

## Países restringidos

**Supabank NO ofrece servicio a residentes de:**

| País | Motivo |
|------|--------|
| Rusia | Sanciones internacionales |
| Bielorrusia | Sanciones internacionales |
| Irán | Sanciones OFAC |
| Corea del Norte | Sanciones OFAC |
| Siria | Sanciones OFAC |
| Cuba | Sanciones OFAC |
| Venezuela | Sanciones OFAC |
| Myanmar | Sanciones OFAC |

## Restricciones en EE.UU.

**No disponible en:**
- Nueva York (requisitos de BitLicense)
- Texas (requisitos de licencia adicionales)

Todos los demás estados de EE.UU. están admitidos.

## Cómo verificamos la ubicación

Durante el registro, verificamos tu ubicación mediante:
- Geolocalización por IP
- País de emisión del documento
- Verificación de dirección residencial
- Comprobación en listas de sanciones OFAC

## Notas importantes

⚠️ **Uso de VPN**: Usar una VPN para evadir restricciones geográficas está prohibido y resultará en la cancelación de la cuenta.

⚠️ **Fraude documental**: Presentar documentos falsos es ilegal y resultará en un bloqueo permanente.

## ¿Te mudas de país?

Si te trasladas a otro país:
1. Actualiza tu perfil con la nueva dirección
2. Es posible que necesites verificar tu identidad nuevamente
3. Algunas funciones pueden cambiar según tu nueva ubicación

## ¿Preguntas?

Contacta con soporte si no estás seguro sobre la disponibilidad en tu región.
    `.trim(),
  },
  {
    id: "gs-005",
    slug: "account-security-basics",
    title: "Fundamentos de seguridad de la cuenta",
    description: "Prácticas esenciales para proteger tu cuenta",
    category: "getting-started",
    order: 5,
    keywords: ["seguridad", "contraseña", "protección", "seguro", "cuenta"],
    content: `
# Fundamentos de seguridad de la cuenta

Proteger tu cuenta de Supabank es fundamental. Sigue estas prácticas recomendadas para mantener tus fondos seguros.

## Métodos de autenticación

### Enlace mágico (correo)
- Usamos autenticación sin contraseña
- Se envía un enlace seguro a tu correo
- Los enlaces caducan en 15 minutos
- Solo se pueden usar una vez

### Autenticación de dos factores (2FA)
- Añade una capa extra de seguridad
- Requerido para operaciones de alto valor
- Compatible con apps de autenticación

## Buenas prácticas de seguridad

### Seguridad del correo
- Usa un correo único y seguro para Supabank
- Activa 2FA en tu cuenta de correo
- Nunca compartas los enlaces de inicio de sesión
- No uses ordenadores públicos para acceder

### Seguridad del dispositivo
- Mantén tus dispositivos actualizados
- Usa bloqueo de pantalla en el móvil
- Solo usa redes de confianza
- No guardes datos de acceso en navegadores

### Reconocer estafas
- Nunca te pediremos tu contraseña
- Nunca solicitaremos fondos por chat
- Verifica las URLs (solo supabank.xyz)
- No hagas clic en enlaces sospechosos

## Gestión de sesiones

- Las sesiones caducan automáticamente tras inactividad
- Puedes ver las sesiones activas en ajustes
- Cierra sesión en todos los dispositivos si sospechas algo
- Te notificamos de nuevos inicios de sesión

## Qué hacemos para protegerte

- Cifrado de extremo a extremo para datos sensibles
- Auditorías de seguridad periódicas
- Sistemas de detección de fraude
- Almacenamiento en frío para activos cripto
- Cumplimiento con estándares SOC 2

## Reportar problemas de seguridad

Si detectas actividad sospechosa:
1. Contacta con soporte inmediatamente
2. No compartas datos de tu cuenta
3. Revisa las transacciones recientes
4. Considera activar 2FA adicional

## ¿Perdiste el acceso?

Si has perdido acceso a tu correo:
1. Contacta con soporte con verificación de identidad
2. Proporciona una prueba alternativa de identidad
3. La recuperación suele tardar 24-48 horas
    `.trim(),
  },
  {
    id: "gs-006",
    slug: "two-factor-authentication",
    title: "Autenticación de dos factores (2FA)",
    description: "Cómo configurar y usar 2FA",
    category: "getting-started",
    order: 6,
    keywords: ["2fa", "dos factores", "autenticación", "seguridad", "authenticator"],
    content: `
# Autenticación de dos factores (2FA)

La autenticación de dos factores añade una capa extra de seguridad a tu cuenta al requerir una segunda forma de verificación.

## ¿Por qué usar 2FA?

- Protege contra accesos no autorizados
- Requerido para transacciones de alto valor
- Práctica de seguridad estándar en la industria
- Mayor tranquilidad para tus fondos

## Métodos de 2FA compatibles

### App de autenticación (Recomendado)
- Google Authenticator
- Authy
- Microsoft Authenticator
- 1Password

### Verificación por correo
- Códigos de un solo uso enviados por correo
- Respaldo si el authenticator no está disponible

## Configurar 2FA

### Paso 1: Activar en Ajustes
1. Ve a Ajustes > Seguridad
2. Haz clic en "Activar 2FA"
3. Elige tu método preferido

### Paso 2: Escanear código QR
1. Abre tu app de autenticación
2. Escanea el código QR mostrado
3. Introduce el código de 6 dígitos para confirmar

### Paso 3: Guardar códigos de respaldo
- Descarga tus códigos de respaldo
- Guárdalos de forma segura offline
- Cada código solo se puede usar una vez
- Úsalos si pierdes tu authenticator

## Cuándo se requiere 2FA

| Acción | 2FA requerido |
|--------|---------------|
| Inicio desde nuevo dispositivo | Sí |
| Cambiar cuenta bancaria | Sí |
| Retiros grandes | Sí |
| Cambios de perfil | A veces |
| Ver transacciones | No |

## ¿Perdiste tu dispositivo 2FA?

1. Usa un código de respaldo para acceder
2. Ve a Ajustes > Seguridad
3. Restablece 2FA y configúralo de nuevo
4. Si no tienes códigos, contacta con soporte

## Buenas prácticas

- Usa una app de autenticación en lugar de SMS
- Haz copia de seguridad de la semilla del authenticator
- Guarda los códigos de respaldo de forma segura
- Actualiza 2FA si cambias de teléfono
- No hagas capturas de pantalla de los códigos QR
- Nunca compartas códigos con nadie
    `.trim(),
  },
  {
    id: "gs-007",
    slug: "mobile-app-setup",
    title: "Configuración en móvil",
    description: "Accede a Supabank desde tu dispositivo móvil",
    category: "getting-started",
    order: 7,
    keywords: ["móvil", "app", "ios", "android", "teléfono"],
    content: `
# Configuración en móvil

Accede a Supabank desde cualquier lugar con nuestra aplicación web optimizada para móvil.

## Acceso desde móvil

Supabank es una Progressive Web App (PWA), lo que significa que puedes añadirla a tu pantalla de inicio para una experiencia similar a una app nativa.

### iOS (Safari)
1. Abre Safari y ve a supabank.xyz
2. Toca el botón Compartir (cuadrado con flecha)
3. Desplázate y toca "Añadir a pantalla de inicio"
4. Nombra "Supabank" y toca Añadir
5. Abre desde tu pantalla de inicio

### Android (Chrome)
1. Abre Chrome y ve a supabank.xyz
2. Toca el menú de tres puntos
3. Selecciona "Añadir a pantalla de inicio"
4. Confirma tocando "Añadir"
5. Abre desde tu pantalla de inicio

## Funciones en móvil

Todas las funciones de escritorio están disponibles:
- Ver saldos y transacciones
- Crear direcciones de liquidación
- Seguir el estado de depósitos
- Gestionar cuentas bancarias
- Contactar con soporte

## Diseño optimizado para móvil

- Interfaz táctil intuitiva
- Optimizado para pantallas pequeñas
- Navegación inferior para fácil acceso
- Gestos de deslizamiento compatibles
- Soporte para modo oscuro

## Notificaciones

Para recibir notificaciones:
1. Permite las notificaciones cuando se te solicite
2. Actívalas en los ajustes de tu dispositivo
3. Gestiona preferencias en los ajustes de la app

Tipos de notificaciones:
- Confirmaciones de depósito
- Actualizaciones de estado de pagos
- Alertas de seguridad
- Anuncios importantes

## Acceso sin conexión

Algunas funciones funcionan sin conexión:
- Ver historial de transacciones en caché
- Acceder a artículos de ayuda
- Ver resumen de cuenta

Nota: Las transacciones requieren conexión a internet.

## Consejos para uso móvil

- Usa una conexión segura y privada
- Activa el bloqueo de pantalla en tu dispositivo
- Cierra sesión en dispositivos compartidos
- Mantén tu navegador actualizado
- No uses WiFi público para transacciones
    `.trim(),
  },
  {
    id: "gs-008",
    slug: "desktop-access",
    title: "Acceso desde escritorio",
    description: "Usar Supabank en tu ordenador",
    category: "getting-started",
    order: 8,
    keywords: ["escritorio", "navegador", "ordenador", "web", "acceso"],
    content: `
# Acceso desde escritorio

Accede a la experiencia completa de Supabank desde el navegador web de tu ordenador.

## Navegadores compatibles

| Navegador | Versión mínima |
|-----------|----------------|
| Chrome | 90+ |
| Firefox | 88+ |
| Safari | 14+ |
| Edge | 90+ |

## Primeros pasos

1. Abre tu navegador
2. Ve a **supabank.xyz**
3. Haz clic en "Iniciar sesión" o "Empezar"
4. Introduce tu correo para el enlace mágico
5. Revisa tu correo y haz clic para acceder

## Funciones de escritorio

### Panel completo
- Historial completo de transacciones
- Analíticas detalladas
- Opciones avanzadas de filtrado

### Atajos de teclado
- \`Cmd/Ctrl + K\` - Búsqueda rápida
- \`Cmd/Ctrl + N\` - Nueva transacción
- \`Esc\` - Cerrar diálogos

### Múltiples pestañas
- Abre varias cuentas
- Compara transacciones
- Flujo de trabajo eficiente

## Configuración óptima de pantalla

Para la mejor experiencia:
- Ancho mínimo de pantalla: 1280px
- Recomendado: 1920x1080 o superior
- Nivel de zoom: 100%

## Configuración del navegador

### Activar para mejor experiencia
- JavaScript
- Cookies (para la sesión)
- Almacenamiento local
- Notificaciones web (opcional)

### Modo privado
Supabank funciona en modo incógnito/privado, pero:
- Tendrás que iniciar sesión cada vez
- Las preferencias no se guardarán
- 2FA puede requerirse más a menudo

## Marcadores

Añade Supabank a tus marcadores:
1. Presiona \`Cmd/Ctrl + D\`
2. Nombra "Supabank"
3. Guarda en tu barra de marcadores
4. Accede con un solo clic

## Solución de problemas

**¿La página no carga?**
- Limpia la caché del navegador
- Desactiva extensiones temporalmente
- Prueba otro navegador

**¿Problemas de inicio de sesión?**
- Revisa la carpeta de spam
- Asegúrate de que las cookies estén habilitadas
- Prueba en modo incógnito
    `.trim(),
  },
];
